import { useHotelAvailability } from '@/components/HotelAvailabilityContext'
import { ClientClassification } from '@monorepo/functions/src/types/client'
import { Pet, PetSize } from '@monorepo/functions/src/types/reservations'

import { getPetsPricing } from '../utils/PetsPricing'
import { useHotelPricingConfig } from './use-hotel-pricing-config'

export function usePetPricing() {
    const { config } = useHotelPricingConfig()
    const { highSeasonPeriods } = useHotelAvailability()

    const getTotalPetsPrice = (pets: Pet[], clientClassification?: ClientClassification): number => {
        if (!config) return 0

        const basePrice = getPetsPricing(pets, config, clientClassification)

        // Note: High season increase is applied at the reservation level, not individual pet level
        // This function returns the base price per day for the pets
        return basePrice
    }

    const getTotalPetsPriceWithHighSeason = (
        pets: Pet[],
        dates: { from: Date; to: Date } | null,
        clientClassification?: ClientClassification,
    ): number => {
        if (!config || !dates) return 0

        const basePrice = getPetsPricing(pets, config, clientClassification)

        // Apply high season increase if applicable
        if (highSeasonPeriods.length > 0) {
            // Simple high season calculation - can be enhanced later
            const nights = Math.ceil((dates.to.getTime() - dates.from.getTime()) / (1000 * 60 * 60 * 24))
            if (nights === 0) return basePrice

            let highSeasonNights = 0
            let currentDate = new Date(dates.from)

            // Count nights that fall in high season
            for (let i = 0; i < nights; i++) {
                const dateStr = currentDate.toISOString().split('T')[0]
                if (highSeasonPeriods.some(period => dateStr >= period.startDate && dateStr <= period.endDate)) {
                    highSeasonNights++
                }
                currentDate.setDate(currentDate.getDate() + 1)
            }

            // Calculate weighted average multiplier
            const regularNights = nights - highSeasonNights
            const highSeasonMultiplier = 1 + config.highSeasonIncrease / 100

            return basePrice * ((regularNights + highSeasonNights * highSeasonMultiplier) / nights)
        }

        return basePrice
    }

    const getSizeFromWeight = (weight: number): PetSize => {
        if (weight <= 10) return 'pequeño'
        if (weight <= 20) return 'mediano'
        return 'grande'
    }

    const BASE_PRICES = config
        ? {
              pequeño: config.smallDogPrice,
              mediano: config.mediumDogPrice,
              grande: config.largeDogPrice,
          }
        : {
              pequeño: 0,
              mediano: 0,
              grande: 0,
          }

    return {
        getTotalPetsPrice,
        getTotalPetsPriceWithHighSeason,
        getSizeFromWeight,
        BASE_PRICES,
        highSeasonPeriods: highSeasonPeriods as Array<{ startDate: string; endDate: string }>,
    }
}
